'use client'

import React from 'react'
import {
  Bot,
  FileText,
  Database,
  MessageSquare,
  CheckCircle,
  XCircle,
  Layers,
  Shield,
  BarChart3,
} from 'lucide-react'

/**
 * AI Visibility Gap Diagrams
 * Visual components for Part 3 of The AI Visibility Stack series.
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Five Layers Diagram — Shows the 5 AI visibility layers that SEO tools do not measure
 */
export function FiveLayersDiagram() {
  const layers = [
    {
      number: 1,
      name: 'Crawl Access',
      description: 'Can AI bots physically reach your pages?',
      seoCovers: false,
      seoNote: 'SEO checks Googlebot only',
      aiSignals: ['GPTBot access', 'ClaudeBot access', 'PerplexityBot access'],
      color: '#008dcc',
      icon: Bot,
    },
    {
      number: 2,
      name: 'Machine-Readable Declarations',
      description: 'Can AI systems read a summary of what your site offers?',
      seoCovers: false,
      seoNote: 'No SEO tool checks this',
      aiSignals: ['llms.txt presence', 'llms-full.txt depth', 'URL accuracy'],
      color: '#8b5cf6',
      icon: FileText,
    },
    {
      number: 3,
      name: 'Entity Markup',
      description:
        'Does your structured data help LLMs understand what your business is?',
      seoCovers: 'partial',
      seoNote: 'SEO validates syntax, not entity quality',
      aiSignals: [
        'Organization schema with KG ID',
        'Entity relationships',
        'Knowledge graph connections',
      ],
      color: '#FDC304',
      icon: Database,
    },
    {
      number: 4,
      name: 'Content Extractability',
      description: 'Is your content structured so LLMs can quote it directly?',
      seoCovers: false,
      seoNote: 'SEO measures readability, not extractability',
      aiSignals: [
        'Front-loaded answers (BLUF)',
        'Speakable schema targets',
        'AEO formatting patterns',
      ],
      color: '#F90B8A',
      icon: Layers,
    },
    {
      number: 5,
      name: 'Citation Presence',
      description: 'Do AI search engines actually mention and cite your brand?',
      seoCovers: false,
      seoNote: 'No traditional SEO tool tracks this',
      aiSignals: [
        'Brand mentions in LLM responses',
        'Hallucination rate',
        'Share of voice across AI platforms',
      ],
      color: '#008dcc',
      icon: MessageSquare,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE 5 LAYERS OF AI VISIBILITY
        </h3>
        <p className='text-sm text-muted-foreground'>
          Traditional SEO tools measure zero of these completely. Each layer
          depends on the ones below it.
        </p>
      </div>

      <div className='flex flex-col gap-3'>
        {layers.map(layer => (
          <div
            key={layer.number}
            className='p-4 rounded-xl bg-background border border-border/60'
          >
            <div className='flex items-start gap-3'>
              <div
                className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                style={{ backgroundColor: `${layer.color}15` }}
              >
                <layer.icon
                  className='w-5 h-5'
                  style={{ color: layer.color }}
                />
              </div>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2 flex-wrap mb-1'>
                  <span
                    className='text-xs font-bold px-2 py-0.5 rounded-full'
                    style={{
                      backgroundColor: `${layer.color}15`,
                      color: layer.color,
                    }}
                  >
                    Layer {layer.number}
                  </span>
                  <p className='text-sm font-semibold text-foreground'>
                    {layer.name}
                  </p>
                </div>
                <p className='text-xs text-muted-foreground mb-2'>
                  {layer.description}
                </p>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                  <div className='p-2 rounded-lg bg-[#F90B8A]/5 border border-[#F90B8A]/20'>
                    <div className='flex items-center gap-1.5 mb-1'>
                      <XCircle className='w-3 h-3 text-[#F90B8A]' />
                      <span className='text-xs font-medium text-[#F90B8A]'>
                        SEO tools
                      </span>
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {layer.seoNote}
                    </p>
                  </div>
                  <div className='p-2 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20'>
                    <div className='flex items-center gap-1.5 mb-1'>
                      <CheckCircle className='w-3 h-3 text-[#008dcc]' />
                      <span className='text-xs font-medium text-[#008dcc]'>
                        AI visibility signals
                      </span>
                    </div>
                    <div className='flex flex-wrap gap-1'>
                      {layer.aiSignals.map(signal => (
                        <span
                          key={signal}
                          className='text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground'
                        >
                          {signal}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-6 p-3 rounded-lg bg-[#FDC304]/5 border border-[#FDC304]/20 text-center'>
        <p className='text-xs text-[#FDC304] font-medium'>
          <Shield className='w-3 h-3 inline mr-1' />A perfect Ahrefs score
          covers zero of these five layers. That is the gap.
        </p>
      </div>
    </div>
  )
}

/**
 * SEO vs AI Audit Comparison — Side-by-side showing what each audit checks
 */
export function SEOvsAIAuditDiagram() {
  const categories = [
    {
      category: 'Bot Access',
      seo: { label: 'Googlebot only', status: 'partial' as const },
      ai: {
        label: 'GPTBot, ClaudeBot, PerplexityBot, Gemini',
        status: 'full' as const,
      },
    },
    {
      category: 'Content Declaration',
      seo: { label: 'sitemap.xml, meta tags', status: 'partial' as const },
      ai: { label: 'llms.txt, llms-full.txt', status: 'full' as const },
    },
    {
      category: 'Structured Data',
      seo: { label: 'Syntax validation only', status: 'partial' as const },
      ai: {
        label: 'Entity quality, KG linkage, relationship depth',
        status: 'full' as const,
      },
    },
    {
      category: 'Brand Monitoring',
      seo: { label: 'Keyword rankings on Google', status: 'partial' as const },
      ai: {
        label: 'LLM citations, hallucination rate, share of voice',
        status: 'full' as const,
      },
    },
    {
      category: 'Content Quality',
      seo: {
        label: 'Word count, readability score',
        status: 'partial' as const,
      },
      ai: {
        label: 'Extractability, BLUF structure, speakable targets',
        status: 'full' as const,
      },
    },
    {
      category: 'Competitive Intel',
      seo: {
        label: 'Keyword gaps, backlink profiles',
        status: 'partial' as const,
      },
      ai: {
        label: 'Source influence maps, prompt share of voice',
        status: 'full' as const,
      },
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          WHAT EACH AUDIT ACTUALLY CHECKS
        </h3>
        <p className='text-sm text-muted-foreground'>
          Traditional SEO audits and AI visibility audits measure fundamentally
          different signals
        </p>
      </div>

      <div className='flex flex-col gap-2'>
        {/* Header */}
        <div className='grid grid-cols-3 gap-2 px-2'>
          <span className='text-xs font-semibold text-muted-foreground uppercase'>
            Signal
          </span>
          <span className='text-xs font-semibold text-[#F90B8A] uppercase text-center'>
            SEO Audit
          </span>
          <span className='text-xs font-semibold text-[#008dcc] uppercase text-center'>
            AI Visibility Audit
          </span>
        </div>

        {categories.map(cat => (
          <div
            key={cat.category}
            className='grid grid-cols-3 gap-2 p-3 rounded-lg bg-background border border-border/60'
          >
            <div className='flex items-center'>
              <span className='text-xs font-medium text-foreground'>
                {cat.category}
              </span>
            </div>
            <div className='flex items-center justify-center'>
              <span className='text-[10px] text-muted-foreground text-center leading-tight'>
                {cat.seo.label}
              </span>
            </div>
            <div className='flex items-center justify-center'>
              <span className='text-[10px] text-foreground/80 text-center leading-tight'>
                {cat.ai.label}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-6 p-3 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20 text-center'>
        <p className='text-xs text-[#008dcc] font-medium'>
          <BarChart3 className='w-3 h-3 inline mr-1' />
          SEO tools give you half the picture. AI visibility audits show the
          other half.
        </p>
      </div>
    </div>
  )
}
